import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Stack, TextField, Typography, Divider,
} from '@mui/material';
import GetData from '../../services/GetData.js';
import PostData from '../../services/PostData.js';
import { tokens } from '../../theme.js';
import { currencyFormatter } from '../Helpers/CurrencyFormat.js';

const TYPE_LABELS = {
  purchase: 'Purchase',
  batch_consume: 'Batch use',
  sku_packaging: 'SKU packaging',
  breakage: 'Breakage',
  adjust: 'Adjustment',
  convert_to_used: 'Marked used',
  reuse_return: 'Returned to used',
};

function formatWhen(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function IngredientStockPanel({ ingredientId, ingredientData, onIngredientUpdated }) {
  const [load, setLoad] = useState(true);
  const [events, setEvents] = useState([]);
  const [batchUsages, setBatchUsages] = useState([]);
  const [error, setError] = useState('');
  const [breakQty, setBreakQty] = useState('');
  const [breakNote, setBreakNote] = useState('');
  const [convertQty, setConvertQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const refresh = async () => {
    setLoad(true);
    setError('');
    const { getData, error: err } = await GetData(
      'api/ingredients/history',
      { ingredientId },
      null, null, null,
    );
    setLoad(false);
    if (err || !getData) {
      setError('Could not load stock history.');
      return;
    }
    const payload = getData.data || getData;
    setEvents(payload.events || []);
    setBatchUsages(payload.batchUsages || []);
  };

  useEffect(() => {
    if (ingredientId != null) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientId]);

  const submitBreakage = async () => {
    setBusy(true);
    setActionMsg('');
    const { success, error: err } = await PostData(
      {
        ingredientId,
        quantity: Number(breakQty),
        note: breakNote,
      },
      'api/ingredients/breakage',
      false,
    );
    setBusy(false);
    if (success) {
      setBreakQty('');
      setBreakNote('');
      setActionMsg('Breakage recorded.');
      await refresh();
      onIngredientUpdated?.();
    } else {
      const msg = err?.response?.data?.error?.message || 'Could not record breakage.';
      setActionMsg(msg);
    }
  };

  const submitConvert = async () => {
    setBusy(true);
    setActionMsg('');
    const { success, error: err } = await PostData(
      {
        ingredientId,
        quantity: Number(convertQty),
        note: 'Washed / returned to reusable pool',
      },
      'api/ingredients/convert-to-used',
      false,
    );
    setBusy(false);
    if (success) {
      setConvertQty('');
      setActionMsg('Moved to used stock.');
      await refresh();
      onIngredientUpdated?.();
    } else {
      const msg = err?.response?.data?.error?.message || 'Could not convert stock.';
      setActionMsg(msg);
    }
  };

  const toggleReusable = async (next) => {
    setBusy(true);
    setActionMsg('');
    const { success, error: err } = await PostData(
      { ingredientId, reusable: next },
      'api/ingredients/set-reusable',
      false,
    );
    setBusy(false);
    if (success) {
      setActionMsg(next ? 'Reusable tracking enabled.' : 'Reusable tracking disabled.');
      onIngredientUpdated?.();
      await refresh();
    } else {
      setActionMsg(err?.response?.data?.error?.message || 'Could not update reusable setting.');
    }
  };

  if (load) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const reusable = Boolean(ingredientData?.reusable);
  const newStock = Number(ingredientData?.newStock) || 0;
  const usedStock = Number(ingredientData?.usedStock) || 0;
  const parStock = Number(ingredientData?.parStock) || 0;

  return (
    <Stack spacing={2.5} sx={{ pb: 1 }}>
      {/* New / used summary */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.bg,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Stock overview</Typography>
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Total on hand: <strong>{parStock}</strong>
            </Typography>
          </Box>
          <Button
            size="small"
            variant={reusable ? 'contained' : 'outlined'}
            disabled={busy}
            onClick={() => toggleReusable(!reusable)}
          >
            {reusable ? 'Reusable on' : 'Mark reusable'}
          </Button>
        </Stack>

        {reusable && (
          <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: tokens.inkMuted }}>
                New
              </Typography>
              <Typography sx={{ fontWeight: 650, fontSize: 18 }}>{newStock}</Typography>
              <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>Full cost</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: tokens.inkMuted }}>
                Used
              </Typography>
              <Typography sx={{ fontWeight: 650, fontSize: 18, color: tokens.accent }}>{usedStock}</Typography>
              <Typography sx={{ fontSize: 11, color: tokens.inkMuted }}>Preferred for batches · $0 COGS</Typography>
            </Box>
          </Stack>
        )}
        {!reusable && (
          <Typography sx={{ mt: 1, fontSize: 12, color: tokens.inkMuted }}>
            Turn on reusable for packaging like mason jars to track new vs used and lower batch cost when jars are reused.
          </Typography>
        )}
      </Box>

      {/* Convert new → used */}
      {reusable && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Return jars to used pool</Typography>
          <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 1 }}>
            After washing production jars, move them from new → used so the next batch prefers them at no packaging cost.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              type="number"
              label="Qty"
              value={convertQty}
              onChange={(e) => setConvertQty(e.target.value)}
              sx={{ width: 100 }}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={busy || !convertQty}
              onClick={submitConvert}
            >
              Mark used
            </Button>
          </Stack>
        </Box>
      )}

      {/* Breakage */}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>Record breakage / loss</Typography>
        <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 1 }}>
          Removes from stock with a note so you are not looking for the item later.
        </Typography>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              type="number"
              label="Qty"
              value={breakQty}
              onChange={(e) => setBreakQty(e.target.value)}
              sx={{ width: 100 }}
            />
            <TextField
              size="small"
              fullWidth
              label="Note (required)"
              placeholder="e.g. Cracked rim while washing"
              value={breakNote}
              onChange={(e) => setBreakNote(e.target.value)}
            />
          </Stack>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={busy || !breakQty || !String(breakNote).trim()}
            onClick={submitBreakage}
            sx={{ alignSelf: 'flex-start' }}
          >
            Remove from stock
          </Button>
        </Stack>
      </Box>

      {actionMsg && (
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>{actionMsg}</Typography>
      )}

      <Divider />

      {/* Batch usages (from batch.inputs + ledger) */}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Batches that used this item</Typography>
        {batchUsages.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
            No batches list this ingredient as an input yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {batchUsages.map((b) => (
              <Box
                key={b.batchId}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${tokens.border}`,
                }}
              >
                <Typography sx={{ fontWeight: 650, fontSize: 13 }}>
                  Batch {b.batchId}
                  {b.amount != null && (
                    <Typography component="span" sx={{ fontWeight: 400, color: tokens.inkMuted, ml: 1 }}>
                      · {b.amount} {b.measurement || ''}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  {formatWhen(b.bornOn || b.dateAdded)}
                  {b.notes ? ` · ${b.notes}` : ''}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Ledger */}
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Stock history</Typography>
        {error && <Typography sx={{ color: tokens.danger, fontSize: 13 }}>{error}</Typography>}
        {!error && events.length === 0 && (
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
            No ledger events yet. New purchases, batch uses, SKU packaging, and breakage will appear here.
          </Typography>
        )}
        <Stack spacing={1}>
          {events.map((ev) => {
            const out = Number(ev.quantityDelta) < 0;
            return (
              <Box
                key={ev._id}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${tokens.border}`,
                  borderLeft: `3px solid ${out ? tokens.danger : tokens.accent}`,
                }}
              >
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ fontWeight: 650, fontSize: 13 }}>
                    {TYPE_LABELS[ev.type] || ev.type}
                    {ev.condition ? ` · ${ev.condition}` : ''}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 13,
                      color: out ? tokens.danger : tokens.accent,
                    }}
                  >
                    {out ? '' : '+'}{ev.quantityDelta}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                  {formatWhen(ev.createdAt)}
                  {ev.relatedBatchId ? ` · Batch ${ev.relatedBatchId}` : ''}
                  {ev.relatedSkuCode ? ` · SKU ${ev.relatedSkuCode}` : ''}
                  {ev.balanceAfter != null ? ` · Bal ${ev.balanceAfter}` : ''}
                  {ev.unitCostApplied != null && Number(ev.quantityDelta) < 0
                    ? ` · ${currencyFormatter(ev.unitCostApplied, 2)}/unit`
                    : ''}
                </Typography>
                {ev.note && (
                  <Typography sx={{ fontSize: 12, mt: 0.25 }}>{ev.note}</Typography>
                )}
                {reusable && (ev.newStockAfter != null || ev.usedStockAfter != null) && (
                  <Typography sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.25 }}>
                    After: new {ev.newStockAfter ?? '—'} · used {ev.usedStockAfter ?? '—'}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
}
